/**
 * The four ways a KPI value can look, and what each one means — in ONE place.
 *
 * The grid, the "How to read a cell" key above it and the hover card on each
 * value all describe the same four states. They used to carry their own copies
 * of the classes, so the key could teach a treatment the grid no longer drew.
 * Now the key's samples and the cells render from these exact classes.
 *
 * The wording is for a reader who is not a software user (DOP-609): what the
 * look says about the number, not what the pipeline did.
 */

import type { KpiCellState } from "../../types/kpi";

/*
 * `unverified` uses the shared `warning` token rather than Tailwind's stock
 * amber, and `overridden` the brand token rather than a repeated hex.
 */
export const KPI_CELL_STATE_CLASSES: Record<KpiCellState, string> = {
	verified: "text-text-base",
	unverified:
		"text-text-base underline decoration-dotted decoration-warning-fg decoration-2 underline-offset-4",
	overridden: "text-[#008782] font-semibold",
	/*
	 * The em-dash means "nothing was extracted here", which is information, not
	 * decoration. `text-subtle` is 4.8:1 — quiet without disappearing.
	 */
	empty: "text-text-subtle",
};

/** Screen-reader wording, appended to the cell's label. */
export const KPI_CELL_STATE_ARIA: Record<KpiCellState, string> = {
	verified: "checked against your file",
	unverified: "not checked yet",
	overridden: "changed by your team",
	empty: "no number found",
};

/** The key's one-line meaning, and the hover card's heading. */
export const KPI_CELL_STATE_TITLE: Record<KpiCellState, string> = {
	verified: "Checked against your file",
	unverified: "Not checked yet",
	overridden: "Changed by your team",
	empty: "No number found",
};

/** The key's longer phrasing where it has room ("…, worth a look"). */
export const KPI_CELL_STATE_KEY_LABEL: Record<KpiCellState, string> = {
	verified: "Checked against your file",
	unverified: "Not checked yet, worth a look",
	overridden: "Changed by your team",
	empty: "No number found",
};

export const KPI_CELL_STATES: readonly KpiCellState[] = [
	"verified",
	"unverified",
	"overridden",
	"empty",
];
